import { LanguageInput } from "@/components/LanguageInput";
import { cn } from "@/utils/cn";
import { Language, LANGUAGES } from "@/utils/language";
import { useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { Const } from "snudbud-backbone-sdk";
import { LanguageLevel } from "../../types/onboarding";
import { LanguageBadge } from "../LanguageBadge";
import { LevelDropdown } from "../LevelDropdown";

// Map language level to proficiency number
export const levelToProficiency = (level: LanguageLevel): number => {
  switch (level) {
    case "beginner":
      return 0;
    case "intermediate":
      return 0.2;
    case "advanced":
      return 0.4;
    case "fluent":
      return 0.6;
  }
};

// Map proficiency number to language level
export const proficiencyToLevel = (proficiency: number): LanguageLevel => {
  if (proficiency < 0.2) return "beginner";
  if (proficiency < 0.4) return "intermediate";
  if (proficiency < 0.6) return "advanced";
  return "fluent";
};

export interface LanguageWithProficiency {
  languageCode: Const.LanguageCode;
  proficiency: number;
}

interface LanguageWithLevel {
  language: Language;
  level?: LanguageLevel;
}

interface LanguagesSelectorProps {
  languages: LanguageWithProficiency[];
  onChange: (languages: LanguageWithProficiency[]) => void;
  withProficiency?: boolean;
  className?: string;
}

export default function LanguagesSelector({
  languages,
  onChange,
  withProficiency = true,
  className = "",
}: LanguagesSelectorProps) {
  const [showLanguageInput, setShowLanguageInput] = useState(languages.length === 0);
  const [selectedLevel, setSelectedLevel] = useState<LanguageLevel>("beginner");

  // Show input field automatically when languages array becomes empty
  useEffect(() => {
    if (languages.length === 0) {
      setShowLanguageInput(true);
    }
  }, [languages]);

  // Convert languages from API format to UI format
  const languagesWithObjects: LanguageWithLevel[] = languages
    .map((lang) => {
      const language = LANGUAGES.find((l) => l.code === lang.languageCode);
      if (!language) return null;

      return {
        language,
        level: withProficiency ? proficiencyToLevel(lang.proficiency) : undefined,
      };
    })
    .filter(Boolean) as LanguageWithLevel[];

  const addLanguage = (selectedLanguage: Language) => {
    // Check if already selected
    if (languages.some((item) => item.languageCode === selectedLanguage.code)) return;

    const newLanguage: LanguageWithProficiency = {
      languageCode: selectedLanguage.code,
      proficiency: withProficiency ? levelToProficiency(selectedLevel) : 1.0,
    };

    onChange([...languages, newLanguage]);
    setSelectedLevel("beginner");
    setShowLanguageInput(false);
  };

  const removeLanguage = (index: number) => {
    const newLanguages = [...languages];
    newLanguages.splice(index, 1);
    onChange(newLanguages);
  };

  const updateLanguageLevel = (index: number, newLevel: LanguageLevel) => {
    if (!withProficiency) return;

    const newLanguages = [...languages];
    newLanguages[index].proficiency = levelToProficiency(newLevel);
    onChange(newLanguages);
  };

  // Get codes of already selected languages to exclude them from selection
  const excludeLanguages = languages.map((lang) => lang.languageCode);

  return (
    <div className={className}>
      <div className="flex flex-col gap-4">
        {languagesWithObjects.map((item, index) => (
          <div key={`${item.language.code}-${index}`} className="flex flex-row items-center gap-2 flex-wrap">
            <LanguageBadge
              language={item.language}
              level={item.level ? item.level.charAt(0).toUpperCase() + item.level.slice(1) : undefined}
              onRemove={() => removeLanguage(index)}
              className="mb-1"
            />
            {withProficiency && item.level && (
              <LevelDropdown value={item.level} onChange={(level) => updateLanguageLevel(index, level)} />
            )}
          </div>
        ))}

        {showLanguageInput ? (
          <div className="flex flex-row gap-2 flex-wrap">
            <LanguageInput
              onSelect={(lang) => addLanguage(lang)}
              excludeLanguages={excludeLanguages}
              placeholder="Select a language"
              className="flex-1 min-w-[180px] mb-1"
            />
            {withProficiency && <LevelDropdown value={selectedLevel} onChange={setSelectedLevel} />}
          </div>
        ) : (
          <button
            onClick={() => setShowLanguageInput(true)}
            className={cn(
              "flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
            )}
          >
            <FiPlus size={16} />
            Add a language
          </button>
        )}
      </div>
    </div>
  );
}
